import { FeatureToggles } from '@/features/feature-toggles'
import { type Job } from '@/features/jobs/Job'
import { type BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import { action, computed } from 'mobx'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { BookkeepingCompleteCycleDialogViewModel } from '@/features/jobs/details/components/BookkeepingCompleteCycleDialog/BookkeepingCompleteCycleDialogViewModel'
import { type RequestScopeChangesDialogViewModel } from '@/features/jobs/details/components/RequestScopeChangesDialog/RequestScopeChangesDialogViewModel'
import { BookkeepingCompleteJobDialogViewModel } from '@/features/jobs/details/components/BookkeepingCompleteJobDialog/BookkeepingCompleteJobDialogViewModel'
import { type JobStore } from '@/features/jobs/JobStore'
import { type JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { TabController } from '@/infra/TabController'
import { BookkeepingCycleDetailsDialogViewModel } from '@/features/jobs/details/components/BookkeepingCycleDetailsDialog/BookkeepingCycleDetailsDialogViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { task } from 'mobx-task'
import { distinctUntilChanged, switchMap } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type BookkeepingGrantAccessDialogViewModel } from '@/features/jobs/details/components/BookkeepingGrantAccessDialog/BookkeepingGrantAccessDialogViewModel'

/**
 * Bookkeeping tracker card tab.
 */
export type BookkeepingTrackerCardTab = 'FinishedItems' | 'Cycles'

/**
 * What the progression card shows for a bookkeeping step.
 */
export interface BookkeepingStepInfo {
  title: string
  description: string
  tooltip?: string
  percentDone: number
  percentPending: number
  primaryActionText?: string
  primaryAction?: () => void
  secondaryActionText?: string
  secondaryAction?: () => void
}

export type BookkeepingStep =
  /**
   * The job is not a bookkeeping job or is not in a valid state
   */
  | 'NotApplicable'
  /**
   * Firm Admin Perspective: The job hasn't been picked up
   */
  | 'AwaitingPickUp'
  /**
   * Pro and Firm Admin Perspective: The bookkeeping tracker hasn't synced
   */
  | 'SettingUpJob'
  /**
   * Pro Perspective: The client and the admin haven't confirm the access to the bookkeeping platform
   */
  | 'ManageAccessPending'
  /**
   * Pro Perspective: The pro has to confirm the access to the bookkeeping platform
   */
  | 'UnmanagedConfirmAccess'
  /**
   * Firm Admin Perspective: The pro hasn't confirmed the access to the bookkeeping platform
   */
  | 'AwaitingAccessConfirmation'
  /**
   * Firm Admin Perspective: The pro confirmed the access and will start working on the books soon
   */
  | 'ReadyToStart'
  /**
   * Pro Perspective: The pro can start the bookkeeping cycle
   */
  | 'StartBookkeeping'
  /**
   * Pro Perspective: The cycle is starting
   */
  | 'CycleStarting'
  /**
   * Pro and Firm Admin Perspective: The pro is working on the books
   */
  | 'BookkeepingInProgress'
  /**
   * Pro Perspective: Start the next bookkeeping cycle
   */
  | 'StartNextCycle'
  /**
   * Firm Admin Perspective: The pro completed a bookkeeping cycle
   */
  | 'BookkeepingCycleComplete'
  /**
   * Pro Perspective: The pro completed all the bookkeeping items
   */
  | 'BookkeepingCompleted'
  /**
   * Pro Perspective: The pro completed the job
   */
  | 'JobClosed'
  /**
   * Pro Perspective
   */
  | 'ScopeChangeRequestPending'
  /**
   * Client Perspective: The client has to grant the Pro access to their books
   */
  | 'UnmanagedGrantAccess'
  /**
   * Pro and Firm Admin Perspective: The client hasn't granted the Pro access
   * to their books
   */
  | 'AwaitingClientAccess'

/**
 * View model for the bookkeeping progression card: the current step, its cycles and work items.
 */
export class BookkeepingProgressionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Bookkeeping progression steps for the Pro
   */
  private readonly _proSteps: Partial<
    Record<BookkeepingStep, BookkeepingStepInfo>
  > = {
    SettingUpJob: {
      title: 'Setting up the job',
      description:
        'We are currently processing this job. Please check back in a few minutes.',
      percentDone: 0,
      percentPending: 100,
    },
    ManageAccessPending: {
      title: 'Access pending',
      description: 'Customer to provide access to their books.',
      percentDone: 0,
      percentPending: 20,
    },
    AwaitingClientAccess: {
      title: 'Access pending',
      description:
        'The client needs to grant you access to their books. We will let you know once they have.',
      percentDone: 0,
      percentPending: 20,
    },
    UnmanagedConfirmAccess: {
      title: 'Confirm you have access',
      description: `Please confirm that you can access the client's books.`,
      percentDone: 0,
      percentPending: 20,
      primaryActionText: 'Confirm Access',
      primaryAction: () =>
        void this.confirmUnmanagedPlatformAccess().catch(() => undefined),
    },
    StartBookkeeping: {
      title: 'Start bookkeeping',
      description:
        'Start a months worth of bookkeeping. Your client will be notified that you are actively working on their books.',
      percentDone: 60,
      percentPending: 20,
      primaryActionText: 'Start Bookkeeping',
      primaryAction: () =>
        void this.startNewBookkeepingCycle().catch(() => undefined),
    },
    CycleStarting: {
      title: 'Cycle starting',
      description: 'The cycle is starting, please wait.',
      percentDone: 0,
      percentPending: 100,
    },
    BookkeepingInProgress: {
      title: 'Bookkeeping in progress',
      description:
        'Once you have completed the books for the month(s), complete this step.',
      percentDone: 80,
      percentPending: 20,
      primaryActionText: 'Bookkeeping Cycle Completed',
      primaryAction: () => void this.bookkeepingCompleteCycleVm.modal.show(),
    },
    StartNextCycle: {
      title: 'Start next cycle',
      description:
        'Start a new month of bookkeeping after the accounting month ends. Confirm with your customer that all transactions for the month are posted. Once started, they’ll be notified you’re actively working on their books.',
      tooltip:
        'A cycle is the period at the beginning of each month when bookkeeping for the previous month takes place.',
      percentDone: 80,
      percentPending: 20,
      primaryActionText: 'Start Cycle',
      primaryAction: () =>
        void this.startNewBookkeepingCycle().catch(() => undefined),
    },
    BookkeepingCompleted: {
      title: 'All bookkeeping items completed',
      description:
        'Confirm if the customer wants to extend their bookkeeping job. If yes, add a job scope change request. If not, complete the job.',
      percentDone: 100,
      percentPending: 0,
      primaryActionText: 'Complete Job',
      primaryAction: () => this.bookkeepingCompleteJobVm.modal.show(),
      secondaryActionText: 'Add Scope Change',
      secondaryAction: () => this._requestScopeChangesVm.show(),
    },
    ScopeChangeRequestPending: {
      title: 'Scope change request pending',
      description:
        'An admin is reviewing your request. Wait until the admin approves your request to start a new cycle.',
      percentDone: 80,
      percentPending: 20,
    },
    JobClosed: {
      title: 'Job closed',
      description: 'Congratulations! The job is complete.',
      percentDone: 100,
      percentPending: 0,
    },
  }

  /**
   * Bookkeeping progression steps for the client
   */
  private readonly _clientSteps: Partial<
    Record<BookkeepingStep, BookkeepingStepInfo>
  > = {
    SettingUpJob: {
      title: 'Setting up the job',
      description:
        'We are currently processing this job. Please check back in a few minutes.',
      percentDone: 0,
      percentPending: 100,
    },
    ManageAccessPending: {
      title: 'Access pending',
      description: 'Customer to provide access to their books.',
      percentDone: 0,
      percentPending: 20,
    },
    AwaitingPickUp: {
      title: 'Awaiting pickup',
      description: 'A Pro will be with you shortly.',
      percentDone: 0,
      percentPending: 100,
    },
    UnmanagedGrantAccess: {
      title: 'Grant access to your books',
      description:
        'Give your Pro access to your bookkeeping software so they can get started on your books.',
      percentDone: 0,
      percentPending: 20,
      primaryActionText: 'Grant Access',
      primaryAction: () => this._bookkeepingGrantAccessVm.show(),
    },
    AwaitingClientAccess: {
      title: 'Access pending',
      description:
        'The client needs to grant the Pro access to their books before bookkeeping can start.',
      percentDone: 0,
      percentPending: 20,
    },
    AwaitingAccessConfirmation: {
      title: 'Awaiting access confirmation',
      description: 'The Pro needs to confirm that they have access to proceed.',
      percentDone: 0,
      percentPending: 100,
    },
    ReadyToStart: {
      title: 'Ready to start',
      description: 'Your Pro will soon start working on your books soon.',
      percentDone: 0,
      percentPending: 50,
    },
    BookkeepingInProgress: {
      title: 'Bookkeeping in progress',
      description: 'Your Pro is currently working on your books.',
      percentDone: 50,
      percentPending: 50,
    },
    BookkeepingCycleComplete: {
      title: 'Bookkeeping cycle completed',
      description:
        'Your Pro has completed the previous bookkeeping cycle. They will start the next cycle within the first few days of next month.',
      percentDone: 100,
      percentPending: 0,
    },
    BookkeepingCompleted: {
      title: 'All bookkeeping items completed',
      description: 'Your Pro has completed all the bookkeeping items.',
      percentDone: 100,
      percentPending: 0,
    },
    ScopeChangeRequestPending: {
      title: 'Scope change request pending',
      description:
        'An admin is reviewing a scope change request associated to your job.',
      percentDone: 80,
      percentPending: 20,
    },
    JobClosed: {
      title: 'Job closed',
      description: 'The job is complete.',
      percentDone: 100,
      percentPending: 0,
    },
  }

  /**
   * View model for the complete bookkeeping cycle.
   */
  readonly bookkeepingCompleteCycleVm: BookkeepingCompleteCycleDialogViewModel

  /**
   * View model for the bookkeeping cycle details modal.
   */
  readonly bookkeepingCycleDetailsVm: BookkeepingCycleDetailsDialogViewModel

  /**
   * View model for completing the bookkeeping job.
   */
  readonly bookkeepingCompleteJobVm: BookkeepingCompleteJobDialogViewModel

  /**
   * TabController for the Bookkeeping tracker card tabs.
   */
  readonly bookkeepingTrackerCardTabController =
    new TabController<BookkeepingTrackerCardTab>(() => [
      {
        tab: 'FinishedItems',
        title: 'Finished items',
        hasError: () => false,
      },
      {
        tab: 'Cycles',
        title: 'Cycles',
        hasError: () => false,
      },
    ])

  constructor(
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
    private readonly _jobStore: JobStore,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    private readonly _requestScopeChangesVm: RequestScopeChangesDialogViewModel,
    private readonly _bookkeepingGrantAccessVm: BookkeepingGrantAccessDialogViewModel,
    private readonly _job: Job,
    private readonly _canManageBookkeeping: () => boolean,
    private readonly _isClientCollaborator: () => boolean,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.bookkeepingCompleteCycleVm =
      new BookkeepingCompleteCycleDialogViewModel(
        this._newAlert,
        this._createAlertErrorHandler,
        this._job.id,
        this._bookkeepingProgressionStore,
      )

    this.bookkeepingCycleDetailsVm = new BookkeepingCycleDetailsDialogViewModel(
      this._job.id,
      this._bookkeepingProgressionStore,
    )

    this.bookkeepingCompleteJobVm = new BookkeepingCompleteJobDialogViewModel(
      this._jobStore,
      this._job,
      this._createAlertErrorHandler,
    )

    this._lifecycle.manage(this.bookkeepingCompleteCycleVm).manage(
      // Submitting the job creates its progression.
      toStream(() => this._job.status).pipe(
        distinctUntilChanged(),
        switchMap(() =>
          withAbortSignal((signal) =>
            this.fetch(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * Returns the bookkeeping progression info
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this._bookkeepingProgressionStore.bookkeepingProgressions.get(
      this._job.id,
    )
  }

  /**
   * Whether the user can view the Cycles tab in the Bookkeeping tracker card.
   */
  @computed
  get canViewBookkeepingCyclesTab() {
    return this._canManageBookkeeping()
  }

  /**
   * Whether there are any cycles in the bookkeeping progression.
   */
  @computed
  get anyCycles() {
    const cyclesList = this.bookkeepingProgressionInfo?.cycles

    return Boolean(cyclesList?.length)
  }

  /**
   * The bookkeeping cycles, most recent first.
   */
  @computed
  get cyclesNewestFirst() {
    return [...(this.bookkeepingProgressionInfo?.cycles ?? [])].reverse()
  }

  /**
   * Whether the current user is a client
   */
  @computed
  get isClientCollaborator() {
    return this._isClientCollaborator()
  }

  /**
   * Whether the current user is the Pro
   */
  @computed
  get isProCollaborator() {
    return this._job.isProviderCollaborator
  }

  /**
   * Whether the job is a bookkeeping job
   */
  @computed
  get isBookkeepingEnabled() {
    return (
      this._job.jobType?.type === 'Bookkeeping' && FeatureToggles.bookkeeping()
    )
  }

  /**
   * Determines what's the current step depending on the stage, access status,
   * and the collaborator type
   */
  @computed
  get bookkeepingStep() {
    // Since we are fetching the progression info only for jobs with type bookkeeping,
    // if there is no progression yet, it means that it hasn't been created
    if (!this.bookkeepingProgressionInfo) {
      return 'SettingUpJob'
    }

    if (this._job.status === 'Closed') {
      return 'JobClosed'
    }

    switch (this.bookkeepingProgressionInfo.stage) {
      case 'Pending':
      case 'ScopeExtensionRequested':
        return 'SettingUpJob'
      case 'ProvisioningAccess':
        switch (this.bookkeepingProgressionInfo.accessStatus) {
          case 'NotRequested':
            if (this.isProCollaborator) {
              return 'SettingUpJob'
            }
            return 'AwaitingPickUp'
          case 'ManagedAccessClientConnectionRequested':
          case 'ManagedAccessClientConfirmationRequested':
          case 'ManagedAccessAdminConfirmationRequested':
            return 'ManageAccessPending'
          case 'UnmanagedAccessProviderConfirmationRequested':
            if (this._job.isProviderCollaborator) {
              return 'UnmanagedConfirmAccess'
            }
            return 'AwaitingAccessConfirmation'
          case 'UnmanagedAccessClientConfirmationRequested':
            if (this.isClientCollaborator) {
              return 'UnmanagedGrantAccess'
            }
            return 'AwaitingClientAccess'
          case 'ManagedAccessConfirmed':
          case 'UnmanagedAccessConfirmed':
            return 'ReadyToStart'
          default:
            return 'NotApplicable'
        }
      // Legacy stage: assessments no longer exist, but trackers persisted before the
      // feature was removed can still report it until the backend migration runs.
      case 'AssessingBooks':
        return 'ReadyToStart'
      case 'ReadyToStart':
        if (this._job.isProviderCollaborator) {
          if (!this.bookkeepingProgressionInfo.cycles.length) {
            return 'StartBookkeeping'
          }
          return 'StartNextCycle'
        }
        return 'ReadyToStart'
      case 'CycleInProgress':
        if (this._currentCycle) {
          switch (this._currentCycle.status.type) {
            case 'InProgress':
              return 'BookkeepingInProgress'
            case 'Completed':
            case 'Canceled':
              if (this._job.isProviderCollaborator) {
                return 'StartNextCycle'
              }
              return 'BookkeepingCycleComplete'
            default:
              return 'CycleStarting'
          }
        }
        return 'ReadyToStart'
      case 'Completed':
        if (this.scopeChangesSettled) {
          return 'BookkeepingCompleted'
        }
        return 'ScopeChangeRequestPending'
      default:
        return 'NotApplicable'
    }
  }

  /**
   * Returns the current step depending on the collaborator type
   */
  @computed
  get bookkeepingStepInfo() {
    if (this.isProCollaborator) {
      return this._proSteps[this.bookkeepingStep]
    }
    return this._clientSteps[this.bookkeepingStep]
  }

  /**
   * Whether the scope changes are settled.
   */
  @computed
  get scopeChangesSettled() {
    return this._jobScopeChangeStore.areSettledForJob(this._job)
  }

  /**
   * Bookkeeping work items
   */
  @computed
  get workItems() {
    return this.bookkeepingProgressionInfo?.workItems.filter(
      (wi) => wi.quantity > 0,
    )
  }

  /**
   * Confirms that the Pro has access to the bookkeeping platform when the
   * access method is Unmanaged
   */
  @action.bound
  async confirmUnmanagedPlatformAccess() {
    await this._bookkeepingProgressionStore
      .confirmUnmanagedPlatformAccess(this._job.id)
      .catch(this._createAlertErrorHandler('Confirming platform access failed'))
  }

  /**
   * Starts a new bookkeeping cycle
   */
  @action.bound
  async startNewBookkeepingCycle() {
    await this._bookkeepingProgressionStore
      .startNewBookkeepingCycle(this._job.id)
      .catch(
        this._createAlertErrorHandler(
          'Starting a new bookkeeping cycle failed',
        ),
      )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.fetch.reset()
    this._bookkeepingGrantAccessVm.modal.close()
  }

  /**
   * Loads the job's bookkeeping progression.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._bookkeepingProgressionStore.fetchBookkeepingProgressionInfo(
      this._job.id,
      signal,
    )
  })

  @computed
  private get _currentCycle() {
    return this.bookkeepingProgressionInfo?.currentCycle
  }
}
